import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';

/**
 * Publishes the device's safe-area insets as the --sai-top/right/bottom/left CSS
 * variables that every screen pads itself by, and exposes the resolved pixel
 * values to JS for layouts that compute their own heights.
 *
 * Why this exists: the CSS `env(safe-area-inset-bottom)` keyword is NOT a
 * cross-platform value. iOS reports the home-indicator area correctly, but
 * Android's WebView only ever populates env() for a display cutout — the
 * navigation bar is invisible to it, so that keyword reads 0 on every Android
 * phone regardless of the nav bar's real height. Combined with the edge-to-edge
 * window the app draws in (forced by the system since targetSdk 35), that put
 * the sheet's buttons physically underneath the 3-button nav bar, where taps
 * land on the nav bar instead of the button. Android now measures its insets
 * natively and feeds them in here; iOS keeps using env() via the CSS fallback.
 *
 * The fallback chain lives in index.css, where each variable defaults to its
 * env() keyword; an inline style set here overrides it. So mobile web, the PWA,
 * and an Android build whose bridge is missing all degrade to the old behaviour
 * rather than breaking.
 */

export type SafeAreaInsets = { top: number; right: number; bottom: number; left: number };

type InsetBridge = {
  getTop(): number;
  getRight(): number;
  getBottom(): number;
  getLeft(): number;
};

declare global {
  interface Window {
    SafariCabInsets?: InsetBridge;
    __safariCabApplyInsets?: (top: number, right: number, bottom: number, left: number) => void;
  }
}

let current: SafeAreaInsets = { top: 0, right: 0, bottom: 0, left: 0 };
const listeners = new Set<(insets: SafeAreaInsets) => void>();

/**
 * Resolve the four variables to real pixels. They can't be read straight off
 * documentElement — getComputedStyle hands back the literal "env(...)" text for
 * the fallback case — so this measures a throwaway element the browser has
 * actually resolved them on.
 */
const measure = (): SafeAreaInsets => {
  // Runs at module load, ahead of render. If the body somehow isn't parsed yet,
  // keep the last known values rather than throwing — this sits on the boot path,
  // so an exception here would take the whole app down.
  if (!document.body) return current;
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:absolute;top:0;left:0;visibility:hidden;pointer-events:none;' +
    'padding:var(--sai-top) var(--sai-right) var(--sai-bottom) var(--sai-left);';
  document.body.appendChild(probe);
  const cs = getComputedStyle(probe);
  const insets = {
    top: parseFloat(cs.paddingTop) || 0,
    right: parseFloat(cs.paddingRight) || 0,
    bottom: parseFloat(cs.paddingBottom) || 0,
    left: parseFloat(cs.paddingLeft) || 0,
  };
  probe.remove();
  return insets;
};

const publish = () => {
  const next = measure();
  if (
    next.top === current.top && next.right === current.right &&
    next.bottom === current.bottom && next.left === current.left
  ) return;
  current = next;
  listeners.forEach((fn) => fn(current));
};

const apply = (top: number, right: number, bottom: number, left: number) => {
  const root = document.documentElement.style;
  root.setProperty('--sai-top', `${top}px`);
  root.setProperty('--sai-right', `${right}px`);
  root.setProperty('--sai-bottom', `${bottom}px`);
  root.setProperty('--sai-left', `${left}px`);
  publish();
};

export const getSafeAreaInsets = (): SafeAreaInsets => current;

/**
 * Stand-in for an Android shell too old to carry the native inset bridge, so the
 * overlap is fixed by a web deploy instead of waiting on a Play review + rollout.
 * It's a guess at a 3-button nav bar rather than a measurement, so it can only be
 * a floor, never an override: the moment the new AAB is installed the bridge
 * supplies the real number and this is skipped entirely.
 *
 * Deliberately biased towards over-padding. Too much bottom padding costs a strip
 * of screen on a gesture-navigation phone; too little puts a button under the nav
 * bar where it cannot be tapped at all.
 */
const LEGACY_ANDROID_NAV_FLOOR_PX = 48;

export const initSafeAreaInsets = () => {
  // Push channel: the native inset listener calls this on rotation, on a
  // gesture/3-button navigation change, and on a foldable being folded or
  // unfolded — which switches to a physically different screen, with different
  // insets, without restarting the activity.
  window.__safariCabApplyInsets = apply;

  const bridge = window.SafariCabInsets;
  let bridged = false;
  if (bridge) {
    // Pull channel: read once at boot. The push above can't carry the initial
    // value — the native listener fires during layout, before the remote page
    // has a JS context to receive it.
    try {
      apply(bridge.getTop(), bridge.getRight(), bridge.getBottom(), bridge.getLeft());
      bridged = true;
    } catch {
      // A malformed bridge must not take the app down — the fallback below stands.
    }
  }

  // iOS and mobile web never call apply(), so seed from the env() fallback. Also
  // covers rotation there, where the notch moves from top to side.
  publish();

  const onViewportChange = () => {
    publish();
    if (!bridged && Capacitor.getPlatform() === 'android' && Capacitor.isNativePlatform()) {
      // Android reports 0 here whatever the nav bar is really doing; hold the
      // floor so nothing lands underneath it.
      if (current.bottom < LEGACY_ANDROID_NAV_FLOOR_PX) {
        document.documentElement.style.setProperty('--sai-bottom', `${LEGACY_ANDROID_NAV_FLOOR_PX}px`);
        publish();
      }
    }
  };

  onViewportChange();
  window.addEventListener('resize', onViewportChange);
  window.addEventListener('orientationchange', onViewportChange);
};

/** Live insets in CSS pixels, for layouts that measure their own heights. */
export const useSafeAreaInsets = (): SafeAreaInsets => {
  const [insets, setInsets] = useState<SafeAreaInsets>(current);
  useEffect(() => {
    setInsets(current); // catch anything published between render and effect
    listeners.add(setInsets);
    return () => { listeners.delete(setInsets); };
  }, []);
  return insets;
};
